/**
 * Identidad ESTABLE por `id` para filas de live-queries (F4-D).
 *
 * Las live-queries (PowerSync/TanStack) devuelven en cada tick un array NUEVO
 * de objetos NUEVOS aunque los valores no cambien. `<For>` de Solid se keyea
 * por REFERENCIA, así que cada eco de sync REMONTA todas las filas — y con
 * ellas muere cualquier estado interactivo interno (diálogos abiertos, foco,
 * subidas en curso). Síntoma real: el diálogo "insertar vídeo" del authoring
 * se cerraba solo ~200ms después de abrirlo (eco del write-path).
 *
 * Este memo conserva la referencia anterior de cada fila cuando sus valores
 * son shallow-iguales: `<For>` solo remonta filas cuyo contenido cambió.
 */
import { createMemo } from "solid-js";

/** Igualdad ESTRUCTURAL para columnas JSON (el eco re-serializa el jsonb del
 * server con otro orden de claves/espaciado — el string difiere, el valor no). */
function jsonEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) {
    return true;
  }
  if (typeof a !== "string" || typeof b !== "string") {
    return false;
  }
  try {
    return canonical(JSON.parse(a)) === canonical(JSON.parse(b));
  } catch {
    return false;
  }
}

function canonical(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(canonical).join(",")}]`;
  }
  if (value !== null && typeof value === "object") {
    const entries = Object.entries(value)
      .toSorted(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${JSON.stringify(k)}:${canonical(v)}`);
    return `{${entries.join(",")}}`;
  }
  return JSON.stringify(value);
}

function shallowEqual(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
  ignore: ReadonlySet<string>,
  jsonFields: ReadonlySet<string>,
): boolean {
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) {
    return false;
  }
  for (const k of ka) {
    if (ignore.has(k)) {
      continue;
    }
    if (jsonFields.has(k)) {
      if (!jsonEqual(a[k], b[k])) {
        return false;
      }
      continue;
    }
    if (!Object.is(a[k], b[k])) {
      return false;
    }
  }
  return true;
}

export interface StableRowsOptions {
  /**
   * Columnas VOLÁTILES que no cuentan como cambio real (p.ej. `updated_at`,
   * que el write-path server-authoritative re-sella con `now()` en cada eco).
   * Si una fila solo difiere en estas columnas, se conserva la referencia
   * anterior (esos campos quedan "congelados" para la UI — documéntalo si tu
   * componente los muestra).
   */
  ignore?: readonly string[];
  /**
   * Columnas de texto JSON (jsonb) que se comparan por ESTRUCTURA, no por
   * string: el eco del server re-serializa el jsonb (orden de claves,
   * espaciado, defaults COALESCE) y el string difiere aunque el valor no.
   */
  jsonFields?: readonly string[];
}

export function stableRows<T extends { id: string }>(
  rows: () => readonly T[] | undefined,
  options?: StableRowsOptions,
): () => T[] {
  const ignore = new Set(options?.ignore ?? []);
  const jsonFields = new Set(options?.jsonFields ?? []);
  let cache = new Map<string, T>();
  const stable = createMemo(() => {
    const next = rows() ?? [];
    const out: T[] = [];
    const nextCache = new Map<string, T>();
    for (const row of next) {
      const prev = cache.get(row.id);
      const kept =
        prev &&
        shallowEqual(
          prev as Record<string, unknown>,
          row as Record<string, unknown>,
          ignore,
          jsonFields,
        )
          ? prev
          : row;
      out.push(kept);
      nextCache.set(row.id, kept);
    }
    cache = nextCache;
    return out;
  });
  return stable;
}
